/**
 * Cookie Store 兼容性实现
 * 支持原生 CookieStore API 和降级到 document.cookie
 */

// 解析 cookie 字符串
function parseCookies(cookieString: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  if (!cookieString) {
    return cookies;
  }

  for (const cookie of cookieString.split(';')) {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  }

  return cookies;
}

// 设置 cookie
function setCookie(name: string, value: string, options: CookieStoreSetOptions = {}): void {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  if (options.path) {
    cookieString += `; path=${options.path}`;
  }
  if (options.expires) {
    const date = new Date(options.expires);
    cookieString += `; expires=${date.toUTCString()}`;
  }
  if (options.secure) {
    cookieString += '; secure';
  }
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = cookieString;
}

// 删除 cookie
function deleteCookie(name: string, options?: CookieStoreDeleteOptions): void {
  setCookie(name, '', {
    ...options,
    expires: Date.now() - 86_400_000, // 设置为过去的时间
  });
}

// 创建兼容的 CookieStore 实现
function createCompatibleCookieStore(): CookieStore {
  // 存储事件监听器
  const changeListeners: Array<(event: CookieChangeEvent) => void> = [];

  // 触发变化事件
  function triggerChangeEvent(changed: CookieListItem[], deleted: CookieListItem[]) {
    const event: CookieChangeEvent = { changed, deleted } as unknown as CookieChangeEvent;
    for (const listener of changeListeners) {
      try {
        listener(event);
      } catch (error) {
        console.warn('Cookie change listener error:', error);
      }
    }
  }

  // 增强的 setCookie 函数
  function setCookieWithEvent(name: string, value: string, options?: CookieStoreSetOptions): void {
    setCookie(name, value, options);

    // 触发变化事件
    const changed: CookieListItem[] = [{ name, value }];
    const deleted: CookieListItem[] = [];
    triggerChangeEvent(changed, deleted);
  }

  // 增强的 deleteCookie 函数
  function deleteCookieWithEvent(name: string, options?: CookieStoreDeleteOptions): void {
    const oldValue = parseCookies(document.cookie)[name];
    deleteCookie(name, options);

    // 触发变化事件
    const changed: CookieListItem[] = [];
    const deleted: CookieListItem[] = oldValue ? [{ name, value: oldValue }] : [];
    triggerChangeEvent(changed, deleted);
  }

  return {
    async get(name?: string): Promise<CookieListItem | null> {
      const cookies = parseCookies(document.cookie);
      if (!name) {
        const firstKey = Object.keys(cookies)[0];
        return firstKey ? { name: firstKey, value: cookies[firstKey] } : null;
      }

      const value = cookies[name];
      return value ? { name, value } : null;
    },

    async getAll(name?: string): Promise<CookieListItem[]> {
      const cookies = parseCookies(document.cookie);
      if (!name) {
        return Object.entries(cookies).map(([name, value]) => ({ name, value }));
      }

      const value = cookies[name];
      return value ? [{ name, value }] : [];
    },

    async set(name: string, value: string, options?: CookieStoreSetOptions): Promise<void> {
      setCookieWithEvent(name, value, options);
    },

    async delete(name: string, options?: CookieStoreDeleteOptions): Promise<void> {
      deleteCookieWithEvent(name, options);
    },

    addEventListener(type: 'change', listener: (event: CookieChangeEvent) => void): void {
      if (type === 'change') {
        changeListeners.push(listener);
      }
    },

    removeEventListener(type: 'change', listener: (event: CookieChangeEvent) => void): void {
      if (type === 'change') {
        const index = changeListeners.indexOf(listener);
        if (index !== -1) {
          changeListeners.splice(index, 1);
        }
      }
    },
  } as CookieStore;
}

// 初始化 cookieStore
export function initCookieStore(): void {
  try {
    // 检查是否已经存在 cookieStore
    if ('cookieStore' in window) {
      const cookieStore = window.cookieStore;
      // 检查是否是有效的 CookieStore 对象
      if (
        typeof cookieStore === 'object' &&
        cookieStore !== null &&
        typeof cookieStore.get === 'function' &&
        typeof cookieStore.set === 'function'
      ) {
        console.info('使用原生 CookieStore API');
        return;
      }
    }

    // 如果不存在或不是有效的 CookieStore，尝试设置兼容性实现
    console.info('使用兼容性 CookieStore 实现');

    // 使用 Object.defineProperty 来安全地设置属性
    Object.defineProperty(window, 'cookieStore', {
      value: createCompatibleCookieStore(),
      writable: false,
      configurable: true,
      enumerable: true,
    });
  } catch (error) {
    console.warn('无法设置 cookieStore，可能已被浏览器定义:', error);
    // 如果无法设置，我们可以创建一个全局变量作为备选方案
    (window as any).__cookieStoreFallback = createCompatibleCookieStore();
  }
}

// 获取可用的 cookieStore 实例
export function getCookieStore(): CookieStore {
  // 优先使用原生的 cookieStore
  if (
    'cookieStore' in window &&
    window.cookieStore &&
    typeof window.cookieStore === 'object' &&
    typeof (window.cookieStore as CookieStore).get === 'function'
  ) {
    return window.cookieStore as CookieStore;
  }

  // 使用备选实现
  if ('__cookieStoreFallback' in window && window.__cookieStoreFallback) {
    return window.__cookieStoreFallback;
  }

  // 如果都没有，创建一个新的实例
  const fallback = createCompatibleCookieStore();
  (window as any).__cookieStoreFallback = fallback;
  return fallback;
}

// 导出兼容性实现
export { createCompatibleCookieStore };
